import React from 'react';
import { Button } from 'ui';

import useTutorToast from './useTutorToast';

import './_tutor-toast.scss';

type TutorToastProps = {
  autoHideDuration?: number;
  /** Inline content: it goes in the same paragraph as the title, so readers read both in one go. */
  children: React.ReactNode;
  closeLabel: string;
  onClose: () => void;
  /** Focus is never in the toast when time is up: unlike `onClose`, it must not be moved. */
  onTimeout: () => void;
  title: string;
};

/**
 * Message from the tutor next to the floating robot (Figma «fake-toast»). Where it sits is up to
 * the robot. Not a dialog and not a live region: whoever shows it decides whether to announce it.
 */
function TutorToast({ autoHideDuration = 7000, children, closeLabel, onClose, onTimeout, title }: TutorToastProps): JSX.Element {
  const { className, onBlur, onFocus, onMouseEnter, onMouseLeave, style, textId } = useTutorToast({ autoHideDuration, onTimeout });

  // Tab only stops at the X, so it carries the message as its description: otherwise a keyboard user
  // with a screen reader hears «Close notice» without knowing what it closes.
  return (
    <div className={className} onBlur={onBlur} onFocus={onFocus} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} style={style}>
      <p className="tutor-toast__text" id={textId}>
        <strong className="tutor-toast__title">{title}</strong> {children}
      </p>
      <Button
        aria-describedby={textId}
        ariaLabel={closeLabel}
        className="tutor-toast__close"
        isIconOnly
        leftIcon="close"
        onClick={onClose}
        size="xs"
        variant="plain"
      />
    </div>
  );
}

export default TutorToast;
